import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as Animatable from 'react-native-animatable';
import { View, StyleSheet, Dimensions } from 'react-native';
import Utils from '../../lib/Utils';

import colors from '../../constants/colors';

const RefContext = React.createContext({});

const Toggle = ({ children }) => {
  const ref = useRef(null)
  const {setRefToggle, refFloating} = useContext(RefContext);

  useEffect(() => {
    setRefToggle(ref);
  }, [ref])

  return (
    <View ref={ref}>{children}</View>
  )
}

const Floating = (props) => {
  const ref = useRef(null);
  const backgroundRef = useRef(null)
  const {setRefFloating, refToggle} = useContext(RefContext);

  const [overload, setOverload] = useState(null);
  const [toggleDimensions, setToggleDimensions] = useState(null);
  const [firstRender, setFirstRender] = useState(true);

  let windowDimension = Dimensions.get('window');

  useEffect(() => {
    setRefFloating(ref);
  }, [ref])

  useEffect(() => {
    if(props.isOpen) {
      if (refToggle && refToggle.current) {
        refToggle.current.measure((fx, fy, width, height, px, py) => {
          setToggleDimensions({x: px, y: py, width: width, height: height});
        });
      }
      if (firstRender) setFirstRender(false);
    }
    else {
      if (props.animated && ref.current.animate && ref && ref.current) {
        onRequestClose()
      } else {
        setToggleDimensions(null)
        setOverload(null)
      }
    }
  }, [props.isOpen])

  const onRequestClose = async () => {
    backgroundRef.current.animate("fadeOut", 500);
    await ref.current.animate(props.endAnimation || "fadeOut", 100);
    setToggleDimensions(null)
    setOverload(null)
  }

  useEffect(() => {
    if (ref.current.measure && toggleDimensions) {
      ref.current.measure((fx, fy, width, height, px, py) => {

        let newOverload   = {}
        let floatingBound = floatingPadding() * 2 + floatingMargin() * 2

        if(windowDimension.width <= toggleDimensions.x + width && !props.disableRightOverload) {
          newOverload = { right: true, width: width + floatingBound }
        } else {
          newOverload = { right: false, width: null }
        }

        if(windowDimension.height <= toggleDimensions.y + height + 30 && !props.disableBottomOverload) {
          newOverload = { ...newOverload, bottom: true, height: height + floatingBound }
        } else {
          newOverload = { ...newOverload, bottom: false, height: null }
        }

        setOverload(newOverload)
      })
    }
  }, [toggleDimensions])

  const floatingPadding = () => {
    if (props.children.props.style) {
      let padding = Utils.flattenObjects(props.children.props.style).padding
      return padding ? padding : 0
    }

    return 0;
  }

  const floatingMargin = () => {
    if (props.children.props.style) {
      let margin = Utils.flattenObjects(props.children.props.style).margin
      return margin ? margin : 0
    }

    return 0;
  }

  if (toggleDimensions && !firstRender && props.animated && overload) {
    return ReactDOM.createPortal(
      <Animatable.View
        duration={200}
        animation={'fadeIn'}
        ref={backgroundRef}
        style={!props.doNotBlockBackground && styles({}).modalBackground}
      >
        <Animatable.View
          ref={ref}
          duration={500}
          style={[
            styles(toggleDimensions).floatingContainer,
            overload.right && {left: toggleDimensions.x - overload.width},
            overload.bottom && {top: toggleDimensions.y - overload.height - 5},
            props.children.props.style && props.children.props.style,
            {zIndex: props.zIndex || 4}
          ]}
          animation={props.isOpen ? props.startAnimation || 'fadeInDownBig' : props.endAnimation || 'fadeOutUpBig'}
        >
          {props.children}
        </Animatable.View>
      </Animatable.View>,
      document.body
    )
  }

  if (toggleDimensions && props.isOpen && !props.animated && overload) {
    return ReactDOM.createPortal(
      <View style={!props.doNotBlockBackground && styles({}).modalBackground}>
        <View
          ref={ref}
          style={[
            styles(toggleDimensions).floatingContainer, 
            overload.right && {left: toggleDimensions.x - (overload.width - toggleDimensions.width)},
            overload.bottom && {top: toggleDimensions.y - overload.height - 5},
            props.children.props.style && props.children.props.style, 
            {zIndex: props.zIndex || 4},
            props.tooltip && {left: toggleDimensions.x - toggleDimensions.width - 10, top: toggleDimensions.y + toggleDimensions.height}
          ]}
        >
          {props.children}
        </View>
      </View>,
      document.body
    )
  } else {
    return ReactDOM.createPortal(
      <View ref={ref} style={{ opacity: 0, top: 0, left: 0, position: 'absolute', zIndex: -1 }}>
        {props.children}
      </View>,
      document.body
    )
  }
}

const Dropdown = (props) => {
  const [refFloating, setRefFloating] = useState(null);
  const [refToggle, setRefToggle] = useState(null);

  useEffect(() => {
    if (!props.disabledClickOutside) {
      document.addEventListener("mousedown", handleClick);
      return () => {
        document.removeEventListener("mousedown", handleClick);
      }
    }
  }, [refFloating])

  const handleClick = (e) => {
    let refChildFloating = refFloating.current ? ReactDOM.findDOMNode(refFloating.current) : null;
    let refChildToggle = refToggle.current ? ReactDOM.findDOMNode(refToggle.current) : null;
    if(refChildFloating && refChildFloating.contains(e.target)) {
      return;
    }
    if ((refChildFloating && !refChildFloating.contains(e.target)) &&
      (refChildToggle && !refChildToggle.contains(e.target))) {
        props.onOutsidePress();
    }
  };

  return (
    <RefContext.Provider value={{refFloating, setRefFloating, refToggle, setRefToggle}}>
      <View>
        {props.children}
      </View>
    </RefContext.Provider>
  )
};

Dropdown.Toggle = Toggle;
Dropdown.Floating = Floating;

const styles = (toggleDimensions) => StyleSheet.create({
  modalBackground: {
    position: "fixed",
    top: 0,
    width: '100%',
    height: '100%',
    backgroundColor: "rgba(0,0,0,0.1)",
    zIndex: 4
  },
  floatingContainer: {
    position: 'fixed',
    left: toggleDimensions.x,
    top: toggleDimensions.y + toggleDimensions.height + 5,
    backgroundColor: colors.side_menu_background,
    borderColor: colors.side_menu_secondary,
    borderWidth: 1,
    borderRadius: 3
  }
})

export default Dropdown;
